import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Paper,
    TextField,
    IconButton,
    Typography,
    Avatar,
    CircularProgress,
    Card,
    CardContent,
    Button,
    Stack,
    Divider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { useSims } from '../context/SimsContext';
import { fetchProducts, fetchCustomers, fetchShifts, fetchDashboardSummary, chatWithAi } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function AISidebar({ currentPage, onClose }) {
    const {
        addInventoryItem,
        addToPosCart,
        setShowRevenueChart,
        getDatabaseDump
    } = useSims();

    const generateGreeting = (page = '') => {
        let greeting = `Hello! I am your AI Inventory Assistant.`;
        if (page.includes('Dashboard') || page.includes('Overview')) {
            greeting += ` I can help with revenue insights and KPIs.`;
        } else if (page.includes('POS') || page.includes('Point of Sale')) {
            greeting += ` I can help manage your current sale.`;
        } else if (page.includes('Inventory')) {
            greeting += ` I can help with stock levels and reordering.`;
        } else if (page.includes('Analytics')) {
            greeting += ` I can help interpret your sales trends.`;
        } else if (page.includes('Schedul') || page.includes('Shift')) {
            greeting += ` I can help optimize your labor costs and staff rotas.`;
        } else if (page.includes('Customer')) {
            greeting += ` I can help analyze customer loyalty and top spenders.`;
        }
        return greeting;
    };

    const [messages, setMessages] = useState([
        { id: Date.now(), text: generateGreeting(currentPage), sender: 'bot', isGreeting: true },
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        let isMounted = true;
        const fetchInsights = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch('/api/ai/insights', {
                    headers: {
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    }
                });
                if (!response.ok) return;
                const insights = await response.json();
                
                if (isMounted && insights && insights.length > 0) {
                    // Filter out noisy/fallback insight types
                    const meaningful = insights.filter(ins =>
                        ins.type !== 'USER_SUGGESTION' &&
                        !(ins.type === 'SALE_ANALYSIS' && ins.insight && ins.insight.includes('AI System Offline'))
                    );

                    if (meaningful.length === 0) return;

                    // Only show the 2 most recent meaningful insights
                    const recent = meaningful.slice(-2).reverse();
                    const insightMsgs = recent.map(ins => ({
                        id: 'insight-' + ins.id,
                        text: `[${ins.type}] ${ins.insight}`,
                        sender: 'bot',
                        isGreeting: false
                    }));
                    
                    setMessages(prev => {
                        const existingIds = new Set(prev.map(m => m.id));
                        const newMsgs = insightMsgs.filter(im => !existingIds.has(im.id));
                        if (newMsgs.length === 0) return prev;
                        return [...newMsgs, ...prev];
                    });
                }
            } catch (err) {
                console.error("Failed to fetch insights", err);
            }
        };
        fetchInsights();

        // Connect to SSE stream for real-time insights. EventSource cannot send an
        // Authorization header, so the JWT is passed as a query parameter.
        const streamToken = localStorage.getItem('token');
        const streamUrl = streamToken
            ? `/api/ai/insights/stream?token=${encodeURIComponent(streamToken)}`
            : '/api/ai/insights/stream';
        const eventSource = new EventSource(streamUrl);
        
        eventSource.addEventListener('insight', (event) => {
            if (!isMounted) return;
            try {
                const ins = JSON.parse(event.data);
                if (ins.type !== 'USER_SUGGESTION' &&
                    !(ins.type === 'SALE_ANALYSIS' && ins.insight && ins.insight.includes('AI System Offline'))) {
                    
                    const insightMsg = {
                        id: 'insight-' + (ins.id || Date.now()),
                        text: `[${ins.type}] ${ins.insight}`,
                        sender: 'bot',
                        isGreeting: false
                    };
                    
                    setMessages(prev => {
                        if (prev.some(m => m.id === insightMsg.id)) return prev;
                        return [...prev, insightMsg];
                    });
                }
            } catch (e) {
                console.error('Error parsing SSE insight data', e);
            }
        });
        
        eventSource.onerror = (err) => {
            // Close stream on error/403 to prevent EventSource from auto-reconnecting continuously
            eventSource.close();
        };

        return () => {
            isMounted = false;
            eventSource.close();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount — not on every page change

    // Removed the automatic greeting injection when page changes so it stops popping in randomly!

    const handleSend = () => {
        if (!input.trim()) return;

        const userMsg = { id: Date.now(), text: input, sender: 'user' };
        setMessages((prev) => [...prev, userMsg]);
        setInput('');
        setIsTyping(true);

        processCommand(input);
    };

    const callAIService = async (message, pageContextData, pageName, onChunk) => {
        try {
            // Build rich context string from pageContextData
            let contextStr = "";
            if (pageContextData) {
                if (pageContextData.products && pageContextData.products.length > 0) {
                    const lowStockItems = pageContextData.products.filter(p => (
                        Number(p.stockQuantity ?? 0) <= Number(p.reorderLevel || p.minStockLevel || 10)
                    ));
                    
                    contextStr += `Total Inventory Products (${pageContextData.products.length} items catalogued):\n`;
                    contextStr += pageContextData.products.map(p => 
                        `- ${p.name} [SKU: ${p.sku || 'N/A'}, Category: ${p.category || 'General'}, Price: $${Number(p.price || 0).toFixed(2)}, In Stock: ${p.stockQuantity ?? 0}${p.reorderLevel ? `, Reorder Level: ${p.reorderLevel}` : ''}]`
                    ).join("\n") + "\n";
                    
                    if (lowStockItems.length > 0) {
                        contextStr += `Low Stock Alerts (${lowStockItems.length} items at or below reorder threshold): ` + 
                            lowStockItems.map(p => `${p.name} (${p.stockQuantity ?? 0} remaining)`).join(", ") + ".\n";
                    }
                }
                if (pageContextData.dashboardSummary) {
                    const s = pageContextData.dashboardSummary;
                    const revenue = s.todayRevenue ?? s.dailySales ?? s.totalRevenue ?? 0;
                    const transactions = s.transactionCount ?? s.orderCount ?? s.totalSales ?? 0;
                    contextStr += `Store Totals: Revenue $${Number(revenue).toFixed(2)}, Completed Transactions: ${transactions}, Products: ${s.totalProducts ?? 'unknown'}, Shifts: ${s.totalShifts ?? 'unknown'}.\n`;
                }
                if (pageContextData.customers && pageContextData.customers.length > 0) {
                    contextStr += `Registered Customers (${pageContextData.customers.length} total). Top customers: ` +
                        pageContextData.customers.slice(0, 5).map(c => `${c.name} (${c.loyaltyPoints || 0} pts, $${Number(c.totalSpent || 0).toFixed(2)} spent)`).join(", ") + ".\n";
                }
                if (pageContextData.shifts && pageContextData.shifts.length > 0) {
                    contextStr += `Staff Shifts Scheduled (${pageContextData.shifts.length} active/upcoming shifts).\n`;
                }
            }
            
            const response = await chatWithAi(message, contextStr);
            
            // Format the response
            let fullReply = response.reply || response.summary || "I'm sorry, I couldn't generate a response.";
            
            onChunk(fullReply);
        } catch (e) {
            console.error("AI Chat Error", e);
            
            const lowerMsg = message.toLowerCase();
            let reply = "I couldn't reach the AI service just now. Please try again in a moment.";
            
            if (lowerMsg.includes("work") || lowerMsg.includes("test") || lowerMsg.includes("status")) {
                reply = "The AI service is not responding right now, so I can't confirm its status. Please try again shortly.";
            } else if (lowerMsg.includes("stock") || lowerMsg.includes("inventory") || lowerMsg.includes("have") || lowerMsg.includes("product") || lowerMsg.includes("item")) {
                if (pageContextData && pageContextData.products && pageContextData.products.length > 0) {
                    const shown = pageContextData.products.slice(0, 20);
                    const items = shown.map(i => `${i.name} (${i.stockQuantity ?? 0} in stock at $${Number(i.price || 0).toFixed(2)})`).join(', ');
                    reply = `I couldn't reach the AI service, but here is the inventory data I loaded directly (${pageContextData.products.length} items):\n` + items +
                        (pageContextData.products.length > shown.length ? '…' : '');
                } else {
                    reply = "I couldn't reach the AI service, and I don't have inventory data loaded in this session.";
                }
            } else if (lowerMsg.includes("hi") || lowerMsg.includes("hello")) {
                reply = "Hello! I'm here, but the AI service is not responding right now.";
            } else if (lowerMsg.includes("sale") || lowerMsg.includes("sell")) {
                reply = "I couldn't reach the AI service. Try the POS screen directly for sales actions.";
            }
            
            onChunk(reply);
        }
    };

    const processCommand = async (text) => {
        const lowerText = text.toLowerCase();
        let responseMsg = null;

        // --- COMMAND PARSING LOGIC (takes priority over AI) ---
        if (lowerText.includes('add') && lowerText.includes('pos')) {
            const itemName = text.replace(/add/i, '').replace(/to pos/i, '').trim();
            responseMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `I can add "${itemName}" to the POS cart. Do you want to proceed?`,
                action: { type: 'ADD_TO_POS', payload: itemName }
            };
        } else if (lowerText.includes('create') && lowerText.includes('item')) {
            responseMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `I'll create a new inventory item. Please confirm details.`,
                action: {
                    type: 'ADD_INVENTORY',
                    payload: { name: 'New Item (AI)', sku: 'AI-999', price: 19.99, quantity: 10 }
                }
            };
        } else if (lowerText.includes('read') && lowerText.includes('database')) {
            responseMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `I will read the entire database state and show it to you. This might be large.`,
                action: { type: 'READ_DB' }
            };
        } else if (lowerText.includes('hide') && lowerText.includes('chart')) {
            responseMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `I will hide the Revenue Chart on the dashboard.`,
                action: { type: 'TOGGLE_CHART', payload: false }
            };
        } else if (lowerText.includes('show') && lowerText.includes('chart')) {
            responseMsg = {
                id: Date.now() + 1,
                sender: 'bot',
                text: `I will show the Revenue Chart on the dashboard.`,
                action: { type: 'TOGGLE_CHART', payload: true }
            };
        } else {
            // AI CONTEXT RETRIEVAL: Concurrently fetch core store context for accurate answers
            let pageContextData = {};
            try {
                // Products is the primary core entity of SIMS — always fetch so AI knows all products and stock levels
                const productsPromise = fetchProducts().catch(err => {
                    console.warn("Could not fetch products for AI context", err);
                    return [];
                });

                // Fetch dashboard financial summary on Dashboard/Analytics or when query mentions finance/sales/revenue/orders
                const needSummary = (
                    !currentPage ||
                    currentPage.includes('Dashboard') ||
                    currentPage.includes('Analytics') ||
                    Boolean(lowerText.match(/sale|revenue|order|money|financial|profit|kpi|metric|performance|trend|today/))
                );
                const summaryPromise = needSummary
                    ? fetchDashboardSummary().catch(() => null)
                    : Promise.resolve(null);

                // Fetch customers when on Customer page or query mentions customers/loyalty
                const needCustomers = (
                    (currentPage && currentPage.includes('Customer')) ||
                    Boolean(lowerText.match(/customer|loyalty|guest|spender|points|vip/))
                );
                const customersPromise = needCustomers
                    ? fetchCustomers().catch(() => [])
                    : Promise.resolve([]);

                // Fetch shifts when on Scheduling page or query mentions shifts/staff/schedule
                const needShifts = (
                    (currentPage && (currentPage.includes('Schedul') || currentPage.includes('Shift'))) ||
                    Boolean(lowerText.match(/shift|schedule|staff|employee|rota|hours|work/))
                );
                const shiftsPromise = needShifts
                    ? fetchShifts().catch(() => [])
                    : Promise.resolve([]);

                const [products, dashboardSummary, customers, shifts] = await Promise.all([
                    productsPromise,
                    summaryPromise,
                    customersPromise,
                    shiftsPromise
                ]);

                pageContextData.products = Array.isArray(products) ? products : [];
                if (dashboardSummary) pageContextData.dashboardSummary = dashboardSummary;
                if (Array.isArray(customers) && customers.length > 0) pageContextData.customers = customers;
                if (Array.isArray(shifts) && shifts.length > 0) pageContextData.shifts = shifts;
            } catch (e) {
                console.error("Failed to fetch context data for AI", e);
            }
            
            const responseId = Date.now() + 1;
            setMessages((prev) => [...prev, {
                id: responseId,
                sender: 'bot',
                text: ''
            }]);
            
            // The model is streaming, so we don't show the generic typing indicator
            setIsTyping(false);

            await callAIService(text, pageContextData, currentPage, (chunk) => {
                setMessages(prev => prev.map(msg => msg.id === responseId ? { ...msg, text: (msg.text || '') + chunk } : msg));
            });
            // Ensure message is never left blank
            setMessages(prev => prev.map(msg => (msg.id === responseId && !msg.text?.trim()) ? { ...msg, text: "I am online and ready to assist you." } : msg));
            setIsTyping(false);
            
            // We return early here because the message was already added and updated via streaming
            return;
        }

        if (responseMsg) {
            setMessages((prev) => [...prev, responseMsg]);
        }
        setIsTyping(false);
    };

    const handleApprove = (action) => {
        let successMsg = "Action completed.";
        switch (action.type) {
            case 'ADD_TO_POS':
                const added = addToPosCart(action.payload);
                successMsg = added ? `Added ${action.payload} to POS.` : `Could not find item ${action.payload}.`;
                break;
            case 'ADD_INVENTORY':
                addInventoryItem(action.payload);
                successMsg = "Item created in inventory.";
                break;
            case 'READ_DB':
                const data = getDatabaseDump();
                successMsg = "Database content: " + JSON.stringify(data).substring(0, 100) + "... (truncated)";
                break;
            case 'TOGGLE_CHART':
                setShowRevenueChart(action.payload);
                successMsg = `Revenue chart ${action.payload ? 'enabled' : 'disabled'}.`;
                break;
            default:
                successMsg = "Unknown action.";
        }
        setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text: successMsg }]);
    };

    const handleReject = () => {
        setMessages(prev => [...prev, { id: Date.now(), sender: 'bot', text: "Action cancelled." }]);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SmartToyIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="primary" fontWeight="bold">AI Assistant</Typography>
                </Box>
                {onClose && (
                    <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {messages.map((msg) => (
                    <Box key={msg.id} sx={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start', maxWidth: '85%' }}>
                            <Avatar sx={{ 
                                bgcolor: msg.sender === 'user' ? 'secondary.main' : 'primary.main', 
                                color: msg.sender === 'user' ? 'white' : 'black',
                                width: 28, height: 28, mx: 1 
                            }}>
                                {msg.sender === 'user' ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                            </Avatar>

                            <Box>
                                <Paper sx={{ 
                                    p: 1.5, 
                                    bgcolor: msg.sender === 'user' ? 'secondary.dark' : 'background.default', 
                                    color: 'text.primary',
                                    borderRadius: 2, 
                                    ...(msg.sender === 'user' ? { borderTopRightRadius: 0 } : { borderTopLeftRadius: 0 }) 
                                }}>
                                    {msg.text === '' ? (
                                        <CircularProgress size={16} color="primary" sx={{ display: 'block', m: 0.5 }} />
                                    ) : (
                                        <Box sx={{ 
                                            wordBreak: 'break-word',
                                            '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                                            '& table': { borderCollapse: 'collapse', width: '100%', mb: 1, fontSize: '0.875rem' },
                                            '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.5 },
                                            '& th': { bgcolor: 'action.hover', fontWeight: 'bold' },
                                            '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                                            '& h1, & h2, & h3': { mt: 1, mb: 0.5 },
                                            fontSize: '0.875rem'
                                        }}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {msg.text}
                                            </ReactMarkdown>
                                        </Box>
                                    )}
                                </Paper>

                                {/* Action Approval Card */}
                                {msg.action && (
                                    <Card sx={{ mt: 1, border: '1px solid', borderColor: 'primary.main', bgcolor: 'background.default' }}>
                                        <CardContent sx={{ pb: 1, p: 1.5 }}>
                                            <Typography variant="caption" color="primary" fontWeight="bold">PROPOSED ACTION</Typography>
                                            <Typography variant="body2">{msg.action.type}: {JSON.stringify(msg.action.payload)}</Typography>
                                        </CardContent>
                                        <Stack direction="row" spacing={1} sx={{ p: 1, pt: 0 }}>
                                            <Button size="small" variant="contained" color="primary" onClick={() => handleApprove(msg.action)}>
                                                Approve
                                            </Button>
                                            <Button size="small" variant="outlined" color="error" onClick={() => handleReject()}>
                                                Reject
                                            </Button>
                                        </Stack>
                                    </Card>
                                )}
                            </Box>
                        </Box>
                    </Box>
                ))}
                {isTyping && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, ml: 4 }}>
                        <Paper sx={{ p: 1, borderRadius: 2, borderTopLeftRadius: 0, bgcolor: 'background.default' }}>
                            <CircularProgress size={16} color="primary" />
                        </Paper>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            <Divider />
            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'background.default', borderRadius: 2, p: 0.5, border: '1px solid', borderColor: 'divider' }}>
                    <TextField
                        fullWidth
                        placeholder="Ask me anything..."
                        variant="standard"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        InputProps={{
                            disableUnderline: true,
                            sx: { px: 1, fontSize: '0.875rem' }
                        }}
                    />
                    <IconButton size="small" color="primary" onClick={handleSend} disabled={!input.trim()}>
                        <SendIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}
